import React from "react";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { lightGreen } from "@mui/material/colors";

import { TableRow } from "../../UI/TableRow";
import { TableCell as MuiTableCell } from "../../UI/TableCell";
import { Checkbox } from "../../UI/Checkbox";
import { TableResidentsCellItem } from "./TableResidentsCellItem";
import { TableRoomsCell } from "./TableRoomsCell";
import { TableUnitCell } from "./TableUnitCell";

import { UnitInternalCommentEdit } from "./UnitInternalCommentEdit";
import { IPlacementWorksheetUnit } from "../../services/PlacementWorksheetApi";
import { ModalProvider } from "../../contexts/ModalContext";
import { ManageButton } from "./ManageButton";
import { usePlacementWorksheetTableRowContext } from "./PlacementWorksheetTableRowContext";
import { UnplacedAllResidentsButton } from "../UnplacedAllResidentsButton";
import { Grid } from "../../UI/Grid";
import { usePlacementWorksheetSettingsContext } from "../../contexts/PlacementWorksheetContext";
import { COLOR_WHITE, COLOR_ATHENS_GRAY } from "../../constants/styles";

// Regular table cell
const TableCell = styled(MuiTableCell)`
  min-width: 120px;
  background-color: ${(props) => props.isOdd ? COLOR_ATHENS_GRAY : COLOR_WHITE} !important;
  transition: background-color 0.2s;
`;

// Left sticky table cell
const LeftStickyTableCell = styled(MuiTableCell)`
  position: sticky;
  left: ${(props) => props.leftPosition || 0}px;
  z-index: 8 !important;
  background-color: ${(props) => props.isOdd ? COLOR_ATHENS_GRAY : COLOR_WHITE} !important;
  transition: box-shadow 0.2s, background-color 0.2s, z-index 0.2s;
  box-shadow: ${(props) => props.isScrolled ? '2px 0 4px -2px rgba(0, 0, 0, 0.15)' : 'none'};
  flex-shrink: 0 !important; /* Prevent column from shrinking */
  flex-grow: 0 !important; /* Prevent column from growing */

  /* Higher z-index when inline edit is active */
  &:has([data-testid="inline-edit--edit-view"]) {
    z-index: 9 !important; /* Higher than sticky headers and other cells */
  }

  /* Alternative selector for when inline edit is in edit mode */
  &:has(input:focus) {
    z-index: 9 !important; /* Higher than sticky headers and other cells */
  }
`;

// Right sticky table cell
const RightStickyTableCell = styled(MuiTableCell)`
  position: sticky !important;
  right: ${(props) => props.rightPosition || 0}px !important;
  z-index: 8 !important; /* Same z-index as left sticky cells in body */
  background-color: ${(props) => props.isOdd ? COLOR_ATHENS_GRAY : COLOR_WHITE} !important;
  transition: box-shadow 0.2s, background-color 0.2s, z-index 0.2s;
  box-shadow: ${(props) => props.isScrolled ? '-2px 0 4px -2px rgba(0, 0, 0, 0.15)' : 'none'};
  white-space: normal !important; /* Prevent text from collapsing */
  overflow: visible !important; /* Ensure content is not cut off */
  box-sizing: border-box !important; /* Include padding in width calculation */
  /* width, min-width, and max-width are now controlled by the sx prop */
  flex-shrink: 0 !important; /* Prevent column from shrinking */
  flex-grow: 0 !important; /* Prevent column from growing */

  /* Ensure the cell is visible and has a clear boundary */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: rgba(0, 0, 0, 0.1);
    opacity: 1; /* Always show the border */
  }

  /* Higher z-index when inline edit is active */
  &:has([data-testid="inline-edit--edit-view"]) {
    z-index: 9 !important; /* Higher than sticky headers and other cells */
  }

  /* Alternative selector for when inline edit is in edit mode */
  &:has(input:focus) {
    z-index: 9 !important; /* Higher than sticky headers and other cells */
  }
`;

interface IPlacementWorksheetTableBodyRowProps {
  data: IPlacementWorksheetUnit;
  isSelected: boolean;
  onSelect: VoidFunction;
  isReadOnly?: boolean;
  matchGroupAtLimit: boolean;
  index: number; // Add index for determining odd/even rows
  isScrolled: boolean; // Add scroll state for shadow effects
}

const bgAnimation = keyframes`
	0% {
		background-position: 0% 50%;
	}
	50% {
		background-position: 100% 50%;
	}
	100% {
		background-position: 0% 50%;
	}
`;

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "isFinished",
})<{ isFinished: boolean }>`
  transition: ${(props) => props.theme.transitions.create("background")};
  background: ${(props) =>
    props.isFinished
      ? `linear-gradient(130deg, ${lightGreen.A200}, ${lightGreen["50"]}, ${lightGreen.A200})`
      : "transparent"} !important;
  background-size: 300% 300%;
  animation: ${props => props.isFinished ? bgAnimation : 'none'} 1s ease infinite;

  /* Ensure sticky cells inherit the row's background when finished */
  &.MuiTableRow-root.Mui-selected {
    background-color: rgba(25, 118, 210, 0.08);
  }

  /* Apply the finished animation background to sticky cells */
  ${(props) => props.isFinished && `
    .MuiTableCell-root {
      background: linear-gradient(130deg, ${lightGreen.A200}, ${lightGreen["50"]}, ${lightGreen.A200}) !important;
      background-size: 300% 300% !important;
      animation: ${bgAnimation} 1s ease infinite !important;
    }
  `}
`;

export const PlacementWorksheetTableBodyRow: React.FC<IPlacementWorksheetTableBodyRowProps> = ({
  data,
  isSelected,
  onSelect,
  isReadOnly,
  matchGroupAtLimit,
  index,
  isScrolled
}) => {
  const { isFinished } = usePlacementWorksheetTableRowContext();
  const { labels } = usePlacementWorksheetSettingsContext();
  const isOdd = index % 2 === 1;

  // Calculate right positions based on whether action column is shown
  const actionColumnWidth = isReadOnly ? 0 : 120;
  const commentsRightPosition = actionColumnWidth;
  const chooseRoomRightPosition = commentsRightPosition + 180; // 180 = comments column width

  return (
    <StyledTableRow isFinished={isFinished}>
      <LeftStickyTableCell leftPosition={0} isOdd={isOdd} isScrolled={isScrolled} sx={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}>
        <Checkbox isChecked={isSelected} onChange={!isReadOnly && onSelect} isDisabled={isReadOnly}/>
      </LeftStickyTableCell>
      <LeftStickyTableCell leftPosition={50} isOdd={isOdd} isScrolled={isScrolled} sx={{ width: '75px', minWidth: '75px', maxWidth: '75px' }}>
        <TableUnitCell unitId={data.unitId} unitNumber={data.unitNumber} isDisabled={isReadOnly}/>
      </LeftStickyTableCell>
      {labels?.map((currentLabel) => (
        <TableCell key={currentLabel.id} isOdd={isOdd} sx={{ minWidth: '150px', textAlign: 'left' }}>
          <TableResidentsCellItem
            unitId={data.unitId}
            roomLabelId={currentLabel.id}
            roomLabelName={currentLabel.label || ''}
            defaultBedroom={currentLabel.label || ''}
            resident={data.occupants && data.occupants.length > 0 ?
              data.occupants.filter((resident) => resident && resident.roomLabelId === currentLabel.id)[0] :
              undefined
            }
            isDisabled={isReadOnly}
          />
        </TableCell>
      ))}
      <RightStickyTableCell rightPosition={chooseRoomRightPosition} isOdd={isOdd} isScrolled={isScrolled} sx={{ width: '145px !important', minWidth: '145px !important', maxWidth: '145px !important', borderLeft: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <ModalProvider>
          <TableRoomsCell residents={data.occupants || []} unitId={data.unitId} matchGroupAtLimit={matchGroupAtLimit} isReadOnly={isReadOnly} />
        </ModalProvider>
      </RightStickyTableCell>
      <RightStickyTableCell rightPosition={commentsRightPosition} isOdd={isOdd} isScrolled={isScrolled} sx={{ minWidth: '180px', width: '180px' }}>
        <UnitInternalCommentEdit unitId={data.unitId} defaultValue={data.comment} isDisabled={isReadOnly}/>
      </RightStickyTableCell>
      {!isReadOnly && (
        <RightStickyTableCell rightPosition={0} isOdd={isOdd} isScrolled={isScrolled}>
          <Grid container spacing={1}>
            <Grid item>
              <ModalProvider>
                <ManageButton unit={data} />
              </ModalProvider>
            </Grid>
            <Grid item>
              <ModalProvider>
                <UnplacedAllResidentsButton unitId={data.unitId} />
              </ModalProvider>
            </Grid>
          </Grid>
        </RightStickyTableCell>
      )}
    </StyledTableRow>
  );
};
